import React from "react";
import { useTable, useSortBy, Column } from "react-table";
import { CustomPagination } from "../../lib";
import { KTIcon } from "../../../../../_metronic/helpers";
import moment from "moment-jalaali";

interface UserLog {
  _id: string;
  userId: string;
  nationalCode: string;
  type: string;
  status: string;
  description: string;
  createdAt: string;
  details: {
    ticketId: string;
    matchId: string;
    profilePlatformId: string;
    _id: string;
  };
  ticket: Ticket;
}

interface Match {
  id: number;
  title: string;
  matchDate: string;
  matchTime: string;
  homeTeam: {
    id: number;
    nameFA: string;
  };
  awayTeam: {
    id: number;
    nameFA: string;
  };
}

interface Ticket {
  id: number;
  status: string;
  price: number;
  payMethod: string;
  match: Match;
  profilePlatform: {
    id: number;
    platform: {
      number: number;
      name: string;
    };
  };
  created_at: string;
  attendee?: {
    name: string;
    national_code: string;
    mobile: string;
  };
  user?: {
    f_name: string;
    l_name: string;
    mobile: string;
    national_code: string;
  };
}

interface User {
  f_name: string;
  l_name: string;
  mobile: string;
  national_code: string;
}

interface TableProps {
  data: {
    userLogs: UserLog[];
    tickets: Ticket[];
    totalRows: number;
    user: User;
  };
  queryString: {
    page: number;
    search?: string;
    numberInPage: number;
  };
  nationalCode: string;
  setQueryString: (page: number) => void;
  refetch: () => void;
}

const AttendeAllLogTable = ({
  data,
  queryString,
  nationalCode,
  setQueryString,
  refetch,
}: TableProps) => {
  const columns = React.useMemo<Column<UserLog>[]>(
    () => [
      {
        Header: <HeaderTextIcon text={"ردیف"} />,
        id: 'row_number',
        Cell: ({ row }) => (
          <span className="rowNum">
            {(queryString.page - 1) * queryString.numberInPage + row.index + 1}
          </span>
        ),
      },
      {
        Header: <HeaderTextIcon text={"نام و نام خانوادگی هوادار"} />,
        id: 'attendee_name',
        Cell: ({ row }) => {
          const ticket = row.original.ticket;
          return (
            <span>
              {ticket?.attendee?.name || '-'}
            </span>
          );
        },
      },
      {
        Header: <HeaderTextIcon text={"کد ملی هوادار"} />,
        id: 'attendee_national_code',
        Cell: ({ row }) => {
          const ticket = row.original.ticket;
          return (
            <span dir="ltr">
              {ticket?.attendee?.national_code || '-'}
            </span>
          );
        },
      },
      {
        Header: <HeaderTextIcon text={"شماره موبایل هوادار"} />,
        id: 'attendee_mobile',
        Cell: ({ row }) => {
          const ticket = row.original.ticket;
          return (
            <span dir="ltr">
              {ticket?.attendee?.mobile || '-'}
            </span>
          );
        },
      },
      {
        Header: <HeaderTextIcon text={"نام و نام خانوادگی خریدار"} />,
        id: 'user_name',
        Cell: ({ row }) => {
          const ticket = row.original.ticket;
          return (
            <span>
              {ticket?.user?.f_name || '-'} {ticket?.user?.l_name || ''}
            </span>
          );
        },
      },
      {
        Header: <HeaderTextIcon text={"کد ملی خریدار"} />,
        id: 'national_code',
        Cell: ({ row }) => {
          const ticket = row.original.ticket;
          return (
            <span dir="ltr">
              {ticket?.user?.national_code || '-'}
            </span>
          );
        },
      },
      {
        Header: <HeaderTextIcon text={"شماره موبایل خریدار"} />,
        id: 'mobile',
        Cell: ({ row }) => {
          const ticket = row.original.ticket;
          return (
            <span dir="ltr">
              {ticket?.user?.mobile || '-'}
            </span>
          );
        },
      },
      {
        Header: <HeaderTextIcon text={"مسابقه"} />,
        id: 'match',
        Cell: ({ row }) => {
          const ticket = row.original.ticket;
          return (
            <span>
              {ticket?.match ? 
                `${ticket.match.homeTeam.nameFA} - ${ticket.match.awayTeam.nameFA}` 
                : '-'}
            </span>
          );
        },
      },
      {
        Header: <HeaderTextIcon text={"جایگاه"} />,
        id: 'platform',
        Cell: ({ row }) => {
          const ticket = row.original.ticket;
          return (
            <span>
              {ticket?.profilePlatform?.platform ? 
                `${ticket.profilePlatform.platform.name}` 
                : '-'}
            </span>
          );
        },
      },
      {
        Header: <HeaderTextIcon text={"وضعیت"} />,
        id: 'status',
        accessor: 'status',
        Cell: ({ row }) => {
          const status = row.original.status;
          const type = row.original.type;
          
          let statusText = 'نامشخص';
          let badgeColor = 'info';

          if (type === 'payment') {
            switch(status) {
              case "0":
                statusText = 'لغو پرداخت';
                badgeColor = 'danger';
                break;
              case "1":
                statusText = 'صدور بلیت';
                badgeColor = 'success';
                break;
              case "2":
                statusText = 'لغو توسط درگاه';
                badgeColor = 'warning';
                break;
            }
          } else if (type === 'request-ticket') {
            switch(status) {
              case "0":
                statusText = 'لغو رزرو';
                badgeColor = 'orange';
                break;
              case "1":
                statusText = 'رزرو شده';
                badgeColor = 'primary';
                break;
            }
          }

          return (
            <span className={`badge badge-light-${badgeColor}`}>
              {statusText}
            </span>
          );
        },
      },
      {
        Header: <HeaderTextIcon text={"قیمت"} />,
        id: 'price',
        Cell: ({ row }) => {
          const ticket = row.original.ticket;
          return (
            <span>
              {ticket ? new Intl.NumberFormat('fa-IR').format(ticket.price) + ' ریال' : '-'}
            </span>
          );
        },
      },
      {
        Header: <HeaderTextIcon text={"تاریخ"} />,
        accessor: "createdAt",
        Cell: ({ value }) => (
          <span>
            {moment(value).format('jYYYY/jMM/jDD HH:mm')}
          </span>
        ),
      },
      {
        Header: <HeaderTextIcon text={"روش پرداخت"} />,
        id: 'payMethod',
        Cell: ({ row }) => {
          const ticket = row.original.ticket;
          return (
            <span>
              {ticket?.payMethod === "online" ? "آنلاین" : ticket?.payMethod || '-'}
            </span>
          );
        },
      },
    ],
    [queryString.page, queryString.numberInPage]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: data.userLogs || [],
    },
    useSortBy
  );

  return (
    <div className={"p-c-30"}>

      <div style={{ overflow: "auto" }}>
        <table
          {...getTableProps()}
          className="table align-middle table-row-dashed fs-6 gy-5"
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-end mt-5">
        <CustomPagination
          activePage={queryString.page}
          setQueryString={setQueryString}
          totalRows={data?.totalRows}
          numberInPage={queryString.numberInPage}
        />
      </div>
    </div>
  );
};

const HeaderTextIcon = ({ text }: { text: string }) => (
  <div className={"d-flex align-items-center justify-content-center gap-2"}>
    <span>{text}</span>
    <KTIcon iconName={"filter"} className={"fs-5"} />
  </div>
);

export default AttendeAllLogTable; 